import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import i18n from '../../i18n'
import { describePostnummerFilter } from '../../components/QueryExplanationBox'
import type { PostnummerDirection } from '../../components/PostnummerFilter'
import { ALL_36_CLASSES } from '../../types/bruktbilfinansiering'
import { CLASSIFICATION_OPTIONS } from './ClassificationFilter'
import type { BruktbilOutletContext } from './FilterSlot'
import { useBruktbilFilters } from './useBruktbilFilters'

/** Up to this many selected classes are named; more are counted (#1014). */
const MAX_NAMED_KLASSER = 3

export interface ExplanationFilterInput {
  klasse: string[]
  omregistrering: boolean
  postnummerEnabled: boolean
  /** The postnumre the query is filtered on, comma-separated; null when none. */
  postnummer: string | null
  postnummerNavn: string | null
  /** Null when the page has no direction (Ståtid filters on the seller only). */
  postnummerDirection: PostnummerDirection | null
  /** Ståtid hides klasse and eierskifte — they don't reach its endpoints (#523). */
  showKlasseOgEierskifte: boolean
  /** The tab's org-type filter; undefined when the tab has none. */
  orgType?: { label: string; value: string | null }
}

/**
 * The explanation box's filter sentences (#1014).
 *
 * Same rule as the collapsed filter summary: every filter is stated, defaults
 * included, so the box never silently leaves one out. It describes the APPLIED
 * filters — what the numbers on screen were built from.
 */
export function explanationFilterLines(i: ExplanationFilterInput): string[] {
  const t = i18n.getFixedT(null, 'bruktbil', 'forklaring')
  const out: string[] = []

  if (i.showKlasseOgEierskifte) {
    const total = ALL_36_CLASSES.length
    if (i.klasse.length === 0 || i.klasse.length >= total) out.push(t('alleKlasser'))
    else if (i.klasse.length <= MAX_NAMED_KLASSER) out.push(t('klasser', { list: i.klasse.join(', ') }))
    else out.push(t('nAvKlasser', { n: i.klasse.length, total }))

    out.push(i.omregistrering ? t('kunOmregistrering') : t('alleEierskifter'))
  }

  if (!i.postnummerEnabled) {
    out.push(t('heleLandet'))
  } else {
    // Enabled but not yet resolved must still read as a restriction, never as
    // "Hele landet" — the same trap filterSummary guards against.
    out.push(describePostnummerFilter(i.postnummer, i.postnummerNavn) ?? t('minePostnumre'))
    if (i.postnummerDirection === 'begge') out.push(t('retningBegge'))
    else if (i.postnummerDirection === 'bilselger') out.push(t('retningSelger'))
    else if (i.postnummerDirection === 'bilkjøper') out.push(t('retningKjoper'))
  }

  if (i.orgType) {
    const opt = CLASSIFICATION_OPTIONS.find(o => o.value === i.orgType!.value)
    const value = opt ? i18n.t(`bruktbil:${opt.labelKey}`) : t('orgTypeAlle')
    out.push(t('orgType', { label: i.orgType.label, value }))
  }

  return out
}

/**
 * The filter sentences for the current page, from the applied filters.
 *
 * `staatid`: Ståtid filters on the seller's postnumre only and ignores klasse
 * and eierskifte, so the sentences follow what its query actually uses.
 * `orgTypeLabel`: set on Selgere/Kjøpere; the line appears only while a tab
 * that has the org-type filter (Topp-selgere/-kjøpere) has registered it.
 */
export function useExplanationFilterLines(opts: { staatid?: boolean; orgTypeLabel?: string } = {}): string[] {
  // Subscribes to language changes; the sentences come from i18n.getFixedT.
  useTranslation('bruktbil')
  const f = useBruktbilFilters()
  const ctx = useOutletContext<BruktbilOutletContext | null>()

  const orgType = opts.orgTypeLabel && ctx && 'classification' in ctx.localApplied
    ? { label: opts.orgTypeLabel, value: ctx.localApplied.classification as string | null }
    : undefined

  return explanationFilterLines(opts.staatid
    ? {
        klasse: [], omregistrering: false,
        postnummerEnabled: !!f.selgerPostnummer, postnummer: f.selgerPostnummer,
        postnummerNavn: f.postnummerNavn, postnummerDirection: null,
        showKlasseOgEierskifte: false,
      }
    : {
        klasse: f.klasse, omregistrering: f.omregistrering,
        postnummerEnabled: f.postnummerEnabled, postnummer: f.selgerPostnummer || f.kjoperPostnummer,
        postnummerNavn: f.postnummerNavn, postnummerDirection: f.postnummerDirection,
        showKlasseOgEierskifte: true, orgType,
      })
}
